import { ReactNode, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { removeToken } from '@shared/services/api';
import Sidebar from '../components/Sidebar';
import Breadcrumb from '../components/Breadcrumb';
import ImpersonationBanner from '../components/ImpersonationBanner';
import SubscriptionBanner from '../components/SubscriptionBanner';
import StorageBanner from '../components/StorageBanner';
import SuspensionBanner from '../components/SuspensionBanner';
import ExitImpersonationModal from '../components/ExitImpersonationModal';
import RateLimitBanner from '../components/RateLimitBanner';
import { NotificationProvider } from '@shared/contexts/NotificationContext';
import { BreadcrumbLabelProvider } from '@shared/contexts/BreadcrumbLabelContext';
import * as Icons from 'lucide-react';

export interface User {
  id: string;
  email: string;
  full_name: string;
  email_verified: boolean;
  role?: string;
  is_platform_admin?: boolean;
  is_suspended?: boolean;
}

interface MainLayoutProps {
  children: ReactNode;
  user: User;
}

export default function MainLayout({ children, user }: MainLayoutProps) {
  const navigate = useNavigate();
  const { t } = useTranslation(['navigation', 'common']);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [desktopSidebarCollapsed, setDesktopSidebarCollapsed] = useState(false);

  const handleLogout = () => {
    removeToken();
    sessionStorage.removeItem('is_impersonating');
    sessionStorage.removeItem('impersonated_user_id');
    sessionStorage.removeItem('impersonated_user_email');
    sessionStorage.removeItem('impersonated_user_name');
    sessionStorage.removeItem('impersonated_by');
    sessionStorage.removeItem('original_admin_token');
    navigate('/login');
  };

  return (
    <NotificationProvider>
      <BreadcrumbLabelProvider>
        <div className="h-screen bg-surface-light dark:bg-surface-dark flex flex-col overflow-hidden font-sans text-slate-700 dark:text-slate-300">
          <a
            href="#main-content"
            className="sr-only focus:not-sr-only focus:absolute focus:z-[60] focus:top-2 focus:left-2 focus:px-4 focus:py-2 focus:rounded-lg focus:bg-brand-600 focus:text-on-accent-light focus:font-bold focus:text-sm"
          >
            {t('common:labels.skipToContent', 'Pular para o conteúdo')}
          </a>
          <div className="flex-shrink-0 relative z-50">
            <RateLimitBanner />
            <ImpersonationBanner />
            <SubscriptionBanner />
            <StorageBanner />
            <SuspensionBanner />
          </div>
          <ExitImpersonationModal />

          {/* Layout Row — sidebar + main content */}
          <div className="flex flex-1 min-h-0 overflow-hidden">
            {/* Desktop Sidebar */}
            <div
              className={`hidden lg:flex lg:flex-col lg:flex-shrink-0 transition-all duration-300 ${desktopSidebarCollapsed ? 'lg:w-20' : 'lg:w-[228px]'}`}
            >
              <Sidebar
                user={user}
                isOpen={true}
                onClose={() => {}}
                onLogout={handleLogout}
                collapsed={desktopSidebarCollapsed}
                onToggleCollapse={() => setDesktopSidebarCollapsed(!desktopSidebarCollapsed)}
              />
            </div>

            {/* Mobile Sidebar */}
            <div
              className={`lg:hidden fixed inset-0 z-50 transition-opacity duration-300 ${sidebarOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}
            >
              <Sidebar
                user={user}
                isOpen={sidebarOpen}
                onClose={() => setSidebarOpen(false)}
                onLogout={handleLogout}
              />
            </div>

            {/* Main Content */}
            <div className="flex-1 flex flex-col min-w-0 overflow-hidden relative">
              {/* Mobile Header — the desktop header is gone; the sidebar carries the brand,
                environment chip, notifications, theme, language and profile. */}
              <header className="lg:hidden flex items-center justify-between h-14 px-4 bg-surface-card-light dark:bg-surface-card-dark border-b border-surface-border-light dark:border-surface-border-dark">
                <button
                  onClick={() => setSidebarOpen(true)}
                  className="p-2 text-text-muted-light dark:text-text-muted-dark hover:bg-surface-2-light dark:hover:bg-surface-2-dark rounded-lg transition-colors"
                  aria-label={t('nav.openMenu')}
                >
                  <Icons.Menu size={24} />
                </button>
                <span className="text-sm font-bold text-slate-900 dark:text-white">pgkb</span>
                <button
                  onClick={() => navigate('/profile')}
                  className="w-9 h-9 flex items-center justify-center"
                  aria-label={t('nav.profile')}
                >
                  <div className="w-7 h-7 rounded-full bg-surface-2-light dark:bg-surface-2-dark flex items-center justify-center text-[11px] font-bold text-text-muted-light dark:text-text-muted-dark">
                    {user?.full_name?.charAt(0).toUpperCase()}
                  </div>
                </button>
              </header>

              <main
                id="main-content"
                className="flex-1 overflow-y-auto focus:outline-none custom-scrollbar"
                style={{ paddingBottom: 'var(--cookie-consent-space, 0px)' }}
              >
                <div className="mx-auto w-full max-w-[1680px] px-4 py-5 sm:px-6 sm:py-6 xl:px-8">
                  {/* Breadcrumbs */}
                  <div className="mb-6">
                    <Breadcrumb />
                  </div>

                  {/* Page Content */}
                  <div>{children}</div>
                </div>
              </main>
            </div>
          </div>
        </div>
      </BreadcrumbLabelProvider>
    </NotificationProvider>
  );
}
